import React, { createContext, useEffect, useReducer } from 'react'
import jwtDecode from 'jwt-decode'
import axios from 'axios.js'
import { MatxLoading } from 'app/components'
import { Store } from '../redux/Store'
import { setUpdateDateOrderPull } from '../redux/actions/SettingsDataOrderPullAction'

const initialState = {
    isAuthenticated: false,
    isInitialised: false,
    user: null,
}

const isValidToken = (accessToken) => {
    if (!accessToken) {
        return false
    }

    const decodedToken = jwtDecode(accessToken)
    const currentTime = Date.now() / 1000
    return decodedToken.exp > currentTime
}

const setSession = (accessToken) => {
    if (accessToken) {
        localStorage.setItem('accessToken', accessToken)
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`
    } else {
        localStorage.removeItem('accessToken')
        delete axios.defaults.headers.common.Authorization
    }
}

const resetOrderAnalyticsPeriod = () => {
    try {
        localStorage.removeItem('orderPeriod')
    } catch (e) {
        console.error(e)
    }
    Store.dispatch(setUpdateDateOrderPull('ToDay'))
}

const syncBarcodeSettings = (user = null) => {
    try {
        if (!user || !window?.localStorage) return
        const src = user?.mapSettings?.barcodeSettings
        let prev = {}
        try {
            const rawPrev = window.localStorage.getItem('pixelcrm_barcode_settings')
            prev = rawPrev ? (JSON.parse(rawPrev) || {}) : {}
        } catch (e) {}
        if (!src && !prev) return
        const payload = {
            prefix: String(src?.prefix ?? prev?.prefix ?? '').replace(/\D+/g, ''),
            body_from: String(src?.body_from ?? prev?.body_from ?? 'art') === 'id' ? 'id' : 'art',
            body_length: Math.max(1, Math.min(11, Number(src?.body_length ?? prev?.body_length ?? 10) || 10)),
            piece_prefix: String(src?.piece_prefix ?? prev?.piece_prefix ?? src?.prefix ?? '21').replace(/\D+/g, '').slice(0, 11),
            weight_prefix: String(src?.weight_prefix ?? prev?.weight_prefix ?? '22').replace(/\D+/g, '').slice(0, 11),
            art_length: Math.max(2, Math.min(10, Number(src?.art_length ?? prev?.art_length ?? 5) || 5)),
            weight_length: Math.max(1, Math.min(8, Number(src?.weight_length ?? prev?.weight_length ?? 5) || 5)),
            weight_decimals: Math.max(0, Math.min(4, Number(src?.weight_decimals ?? prev?.weight_decimals ?? 3) || 3)),
            scan_mode: String(src?.scan_mode ?? prev?.scan_mode ?? 'both') === 'piece_only'
                ? 'piece_only'
                : (String(src?.scan_mode ?? prev?.scan_mode ?? 'both') === 'weight_only' ? 'weight_only' : 'both'),
        }
        window.localStorage.setItem('pixelcrm_barcode_settings', JSON.stringify(payload))
    } catch (e) {
        console.error(e)
    }
}

const reducer = (state, action) => {
    switch (action.type) {
        case 'INIT': {
            const { isAuthenticated, user } = action.payload

            return {
                ...state,
                isAuthenticated,
                isInitialised: true,
                user,
            }
        }
        case 'LOGIN': {
            const { user } = action.payload

            return {
                ...state,
                isAuthenticated: true,
                user,
            }
        }
        case 'LOGOUT': {
            return {
                ...state,
                isAuthenticated: false,
                user: null,
            }
        }
        case 'REGISTER': {
            const { user } = action.payload

            return {
                ...state,
                isAuthenticated: true,
                user,
            }
        }
        default: {
            return { ...state }
        }
    }
}

const AuthContext = createContext({
    ...initialState,
    method: 'JWT',
    login: () => Promise.resolve(),
    logout: () => {},
    register: () => Promise.resolve(),
})

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState)
    const [accessToken, setAccessToken] = React.useState(localStorage.getItem('accessToken'))

    const login = async (email, password) => {
        const response = await axios.post('/api/auth/login', { email, password })
        const { accessToken } = response.data

        setSession(accessToken)
        setAccessToken(accessToken)
        resetOrderAnalyticsPeriod()

        // Получаем профиль, чтобы подтянуть accessPage и остальные поля
        let profileUser = response.data.user || {}
        try {
            const profileResponse = await axios.get('/api/auth/profile')
            profileUser = profileResponse?.data?.user || profileUser
        } catch (err) {
            console.error(err)
        }

        dispatch({
            type: 'LOGIN',
            payload: { user: { ...profileUser, accessPage: profileUser?.accessPage || [] } },
        })
        syncBarcodeSettings(profileUser)
    }

    const register = async (email, username, password, agreements = {}) => {
        const response = await axios.post('/api/auth/register', {
            email,
            username,
            password,
            agreement1: !!agreements?.agreement1,
            agreement2: !!agreements?.agreement2,
            agreement3: !!agreements?.agreement3,
            accept_source: agreements?.accept_source || 'web_register',
        })
        const { accessToken, user } = response.data
        if (!accessToken || !user || !user.id) {
            throw new Error('Регистрация не завершена: сервер не вернул валидные данные авторизации')
        }

        setSession(accessToken)
        setAccessToken(accessToken)

        let profileUser = user || {}
        try {
            const profileResponse = await axios.get('/api/auth/profile')
            profileUser = profileResponse?.data?.user || profileUser
        } catch (err) {
            console.error(err)
        }

        dispatch({
            type: 'REGISTER',
            payload: { user: { ...profileUser, accessPage: profileUser?.accessPage || [] } },
        })
        syncBarcodeSettings(profileUser)
    }

    const logout = () => {
        setSession(null)
        setAccessToken(null)
        resetOrderAnalyticsPeriod()
        dispatch({ type: 'LOGOUT' })
    }

    useEffect(() => {
        (async () => {
            try {
                if (accessToken && isValidToken(accessToken)) {
                    setSession(accessToken)
                    const response = await axios.get('/api/auth/profile')
                    const { user } = response.data

                    dispatch({
                        type: 'INIT',
                        payload: {
                            isAuthenticated: true,
                            user: { ...user, accessPage: user?.accessPage || [] },
                        },
                    })
                    syncBarcodeSettings(user)
                } else {
                    resetOrderAnalyticsPeriod()
                    dispatch({
                        type: 'INIT',
                        payload: {
                            isAuthenticated: false,
                            user: null,
                        },
                    })
                }
            } catch (err) {
                console.error(err)
                resetOrderAnalyticsPeriod()
                dispatch({
                    type: 'INIT',
                    payload: {
                        isAuthenticated: false,
                        user: null,
                    },
                })
            }
        })()
    }, [accessToken]) // Используем состояние accessToken

    if (!state.isInitialised) {
        return <MatxLoading />
    }

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'JWT',
                login,
                logout,
                register,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export default AuthContext
